"use client";
import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Building2, Check, Handshake, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import ContactSalesModal from "@/components/ContactSalesModal";

type PlanKey = "start" | "team" | "business" | "enterprise";

type PlanDef = {
  key: PlanKey;
  ctaVariant: "outline" | "default";
  recommended: boolean;
};

const PLANS: PlanDef[] = [
  { key: "start", ctaVariant: "outline", recommended: false },
  { key: "team", ctaVariant: "default", recommended: true },
  { key: "business", ctaVariant: "outline", recommended: false },
  { key: "enterprise", ctaVariant: "outline", recommended: false },
];

const PricingSection = () => {
  const [praEnabled, setPraEnabled] = useState(false);
  const [salesModalOpen, setSalesModalOpen] = useState(false);
  const { t } = useTranslation();

  const openBooking = () => setSalesModalOpen(true);

  const includedFeatures = t("pricing.included.items", { returnObjects: true }) as string[];
  const partnerBenefits = t("pricing.partnership.benefits", { returnObjects: true }) as string[];
  const directPoints = t("pricing.channels.direct.points", { returnObjects: true }) as string[];
  const partnerChannelPoints = t("pricing.channels.partner.points", { returnObjects: true }) as string[];

  return (
    <>
      <ContactSalesModal open={salesModalOpen} onOpenChange={setSalesModalOpen} />
      <section id="pricing" className="py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-3">
              {String(t("pricing.title"))}
            </h2>
            <p className="text-muted-foreground text-sm max-w-2xl mx-auto">
              {String(t("pricing.subtitle"))}
            </p>
            <p className="text-foreground/90 text-sm max-w-2xl mx-auto mt-3">
              {String(t("pricing.guidance"))}
            </p>
          </motion.div>

          {/* Direct vs partner — commercial framing */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            className="max-w-5xl mx-auto mb-14"
          >
            <p className="text-center text-xs font-semibold uppercase tracking-wide text-primary mb-6">
              {String(t("pricing.channels.eyebrow"))}
            </p>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="rounded-xl border border-border bg-background p-6 md:p-7 shadow-sm flex flex-col">
                <div className="inline-flex items-center gap-2 text-primary mb-3">
                  <Building2 className="h-5 w-5" />
                  <span className="font-display font-semibold text-foreground">
                    {String(t("pricing.channels.direct.title"))}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  {String(t("pricing.channels.direct.description"))}
                </p>
                <ul className="space-y-2 mb-6 flex-1">
                  {directPoints.map((point) => (
                    <li key={String(point)} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                      <span>{String(point)}</span>
                    </li>
                  ))}
                </ul>
                <Button className="w-full sm:w-auto" onClick={openBooking}>
                  {String(t("pricing.channels.direct.cta"))}
                </Button>
              </div>

              <div className="rounded-xl border border-border bg-secondary/30 p-6 md:p-7 shadow-sm flex flex-col">
                <div className="inline-flex items-center gap-2 text-primary mb-3">
                  <Users className="h-5 w-5" />
                  <span className="font-display font-semibold text-foreground">
                    {String(t("pricing.channels.partner.title"))}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  {String(t("pricing.channels.partner.description"))}
                </p>
                <ul className="space-y-2 mb-6 flex-1">
                  {partnerChannelPoints.map((point) => (
                    <li key={String(point)} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                      <span>{String(point)}</span>
                    </li>
                  ))}
                </ul>
                <Button variant="outline" className="w-full sm:w-auto" onClick={openBooking}>
                  {String(t("pricing.channels.partner.cta"))}
                </Button>
              </div>
            </div>
            <p className="text-center text-sm text-muted-foreground mt-5 max-w-2xl mx-auto leading-relaxed">
              {String(t("pricing.channels.footnote"))}
            </p>
          </motion.div>

          {/* Continuity preference (no prices) */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.45 }}
            className="max-w-2xl mx-auto mb-10"
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-xl border border-border bg-secondary/40 px-5 py-4">
              <div className="flex items-center gap-3 shrink-0">
                <Switch
                  id="pra-toggle"
                  checked={praEnabled}
                  onCheckedChange={setPraEnabled}
                  aria-label={String(t("pricing.pra.label"))}
                />
                <label
                  htmlFor="pra-toggle"
                  className="font-display font-semibold text-sm text-foreground cursor-pointer"
                >
                  {String(t("pricing.pra.label"))}
                </label>
              </div>
              <div className="text-left sm:border-l sm:border-border sm:pl-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {String(t("pricing.pra.description"))}
                </p>
                <p className="text-xs text-foreground/80 mt-1.5">
                  {String(t("pricing.pra.partnerNote"))}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Plan cards — commercial, no amounts */}
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5 max-w-6xl mx-auto">
            {PLANS.map((plan, i) => (
              <motion.div
                key={plan.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`relative rounded-xl border p-6 flex flex-col ${
                  plan.recommended
                    ? "border-primary shadow-md ring-1 ring-primary/20"
                    : "border-border shadow-sm"
                } bg-background`}
              >
                {plan.recommended && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-3 py-0.5 rounded-full">
                    {String(t("pricing.recommended"))}
                  </span>
                )}

                <div className="mb-5">
                  <h3 className="font-display font-semibold text-lg text-foreground mb-1">
                    {String(t(`pricing.${plan.key}.name`))}
                  </h3>
                  <p className="text-muted-foreground text-xs mb-3 leading-relaxed">
                    {String(t(`pricing.${plan.key}.audience`))}
                  </p>
                  <p className="text-xs font-medium text-foreground/90 rounded-md border border-border bg-secondary/40 px-3 py-2 mb-4">
                    {String(t(`pricing.${plan.key}.storage`))}
                  </p>

                  <div className="rounded-md border border-dashed border-border/80 px-3 py-2.5 mb-1">
                    <p className="text-sm font-medium text-foreground">
                      {String(t("pricing.commercial.tailored"))}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {praEnabled
                        ? String(t("pricing.commercial.withContinuity"))
                        : String(t("pricing.commercial.backupOnly"))}
                    </p>
                  </div>
                </div>

                <div className="mb-5">
                  <Button variant={plan.ctaVariant} className="w-full" onClick={openBooking}>
                    {String(t(`pricing.${plan.key}.cta`))}
                  </Button>
                </div>

                <ul className="space-y-2 text-sm text-muted-foreground mt-auto">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                    <span>{String(t(`pricing.${plan.key}.highlight`))}</span>
                  </li>
                  {praEnabled && (
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                      <span>{String(t("pricing.pra.cardNote"))}</span>
                    </li>
                  )}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* What's included on every plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-14 max-w-4xl mx-auto"
          >
            <h3 className="font-display text-xl md:text-2xl font-bold text-foreground text-center mb-2">
              {String(t("pricing.included.title"))}
            </h3>
            <p className="text-muted-foreground text-sm text-center mb-8 max-w-2xl mx-auto">
              {String(t("pricing.included.subtitle"))}
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {includedFeatures.map((f) => (
                <li key={String(f)} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                  <span>{String(f)}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Extra storage — commercial, no amounts */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-14 max-w-3xl mx-auto rounded-xl border border-border bg-secondary/30 px-6 py-6 md:px-8"
          >
            <h3 className="font-display text-lg font-bold text-foreground mb-2">
              {String(t("pricing.extraStorage.title"))}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              {String(t("pricing.extraStorage.description"))}
            </p>
            <p className="text-sm text-foreground/90 leading-relaxed">
              {String(t("pricing.extraStorage.example"))}
            </p>
          </motion.div>

          {/* Partnership program */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="mt-16 max-w-5xl mx-auto"
          >
            <div className="relative rounded-xl border border-border bg-background shadow-sm p-8 md:p-10 overflow-hidden">
              <div className="absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l from-secondary/80 to-transparent pointer-events-none hidden md:block" />
              <div className="relative md:flex md:items-start md:gap-10">
                <div className="flex-1 mb-6 md:mb-0">
                  <div className="inline-flex items-center gap-2 text-primary mb-3">
                    <Handshake className="h-5 w-5" />
                    <span className="text-xs font-semibold uppercase tracking-wide">
                      {String(t("pricing.partnership.eyebrow"))}
                    </span>
                  </div>
                  <h3 className="font-display text-xl md:text-2xl font-bold text-foreground mb-2">
                    {String(t("pricing.partnership.title"))}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed max-w-xl">
                    {String(t("pricing.partnership.subtitle"))}
                  </p>
                </div>

                <div className="flex-1">
                  <ul className="space-y-2.5 mb-6">
                    {partnerBenefits.map((b) => (
                      <li key={String(b)} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                        <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                        <span>{String(b)}</span>
                      </li>
                    ))}
                  </ul>
                  <Button onClick={openBooking} className="gap-2">
                    {String(t("pricing.partnership.cta"))}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default PricingSection;
